import {
  DatePicker as BaseDatePicker,
  type DatePickerInputBaseProps,
  type DatePickerRootProps,
  Field,
  Portal,
  parseDate,
} from "@ark-ui/react";
import { CalendarDate, type DateValue } from "@internationalized/date";
import { CalendarBlank, CaretLeft, CaretRight } from "@phosphor-icons/react";
import clsx from "clsx";
import React from "react";
import { useIMask } from "react-imask";
import getFormHelperText, {
  type HelperTextProps,
} from "../../helper/HelperTextIcon.js";
import useMergedRef from "../../hooks/use-merged-ref.js";
import {
  FormLabel,
  type FormLabelClassNames,
  type LabelOptionProps,
} from "../../shared/form-label/FormLabel.js";
import { Button } from "../button/Button.js";
import { IconButton } from "../icon-button/IconButton.js";
import { TextField, type TextFieldProps } from "../textfield/TextField.js";

interface DatePickerProps
  extends Omit<DatePickerRootProps, "onChange" | "onError"> {
  label?: LabelOptionProps;
  size?: "sm" | "md" | "lg";
  onChange?: (date: any) => void;
  error?: TextFieldProps["error"];
  inputVariant?: TextFieldProps["variant"];
  asterisk?: boolean;
  calendarIcon?: React.ReactNode;
  helperText?: HelperTextProps;
  fieldProps?: React.ComponentProps<typeof Field.Root>;
  placeholder?: TextFieldProps["placeholder"];
  labelClassNames?: FormLabelClassNames;
  classNames?: {
    root?: string;
    view?: string;
    label?: string;
    input?: string;
    helperText?: string;
    control?: string;
    content?: string;
    calendar?: string;
    trigger?: string;
  };
  handleInvalidDate?: (errorType: "invalid" | "minDate" | "maxDate") => void;
}

interface WithoutDatePickerFooterProps extends DatePickerProps {
  withFooter?: false;
}

// Props interface for DatePicker with optional footer buttons
interface WithDatePickerFooterProps extends DatePickerProps {
  withFooter?: true;
  labels?: {
    confirm?: string;
    cancel?: string;
  };
  onCancel?: React.MouseEventHandler<HTMLButtonElement>;
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>;
}

type DatePickerComponentProps =
  | WithDatePickerFooterProps
  | WithoutDatePickerFooterProps;

interface DatepickerInputProps
  extends TextFieldProps,
    DatePickerInputBaseProps {}

const rightArrow = <CaretRight size={24} fill="currentColor" />;

const leftArrow = <CaretLeft size={24} fill="currentColor" />;

const DatePickerInput = React.forwardRef<
  HTMLInputElement,
  DatepickerInputProps
>((props, ref) => {
  return <TextField {...props} ref={ref} />;
});

interface ValidationResult {
  isValid: boolean;
  errorType?: "invalid" | "minDate" | "maxDate";
}

const DatePicker = React.forwardRef<
  React.ElementRef<typeof TextField>,
  DatePickerComponentProps
>((props, ref) => {
  const {
    label,
    size = "md",
    helperText = { message: "", status: "default" },
    selectionMode = "single",
    classNames,
    locale = "en-IN",
    min,
    max,
    format,
    timeZone = "UTC",
    error,
    placeholder = "DD/MM/YYYY",
    withFooter = false,
    inputVariant = "outline",
    asterisk = false,
    calendarIcon = <CalendarBlank fill="currentColor" />,
    onChange,
    onValueChange,
    positioning = {
      placement: "bottom-end",
    },
    labelClassNames,
    fieldProps,
    handleInvalidDate,
    ...rootProps
  } = props;

  const {
    labels = {
      cancel: "Cancel",
      confirm: "Action",
    },
    onCancel = () => {},
    onConfirm = () => {},
    ...filteredProps
  } = withFooter ? (rootProps as WithDatePickerFooterProps) : {};

  const effectiveProps = withFooter ? filteredProps : rootProps;

  const handleConfirm = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onConfirm) {
        onConfirm(event);
      }
    },
    [onConfirm],
  );

  const handleCancel = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (onCancel) {
        onCancel(event);
      }
    },
    [onCancel],
  );

  const inputRef = React.useRef<HTMLInputElement>(null);
  // Setup input masking for date format
  const {
    ref: maskedInputRef,
    setValue,
    value,
  } = useIMask(
    {
      mask: "00/00/0000",
    },
    {
      ref: inputRef,
    },
  );

  const effectiveDatePickerRef = useMergedRef(maskedInputRef, ref);

  const validateDateString = React.useMemo(
    () =>
      (dateStr: string, min?: DateValue, max?: DateValue): ValidationResult => {
        // Check if string matches DD/MM/YYYY pattern
        const regex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/;
        if (!regex.test(dateStr)) {
          return { isValid: false, errorType: "invalid" };
        }

        // Split the date string and convert to numbers
        const [day, month, year] = dateStr.split("/").map(Number);

        // Create a date object and verify if the components match
        const date = new Date(year, month - 1, day);
        const isValidComponents =
          date.getDate() === day &&
          date.getMonth() === month - 1 &&
          date.getFullYear() === year;

        if (!isValidComponents) {
          return { isValid: false, errorType: "invalid" };
        }

        // Check min/max constraints if they exist
        const inputDate = new CalendarDate(year, month, day);

        if (min) {
          const minDate = new CalendarDate(min.year, min.month, min.day);
          // Compare using toDate() to ensure accurate comparison
          if (inputDate.toDate(timeZone) < minDate.toDate(timeZone)) {
            return { isValid: false, errorType: "minDate" };
          }
        }

        if (max) {
          const maxDate = new CalendarDate(max.year, max.month, max.day);
          // Compare using toDate() to ensure accurate comparison
          if (inputDate.toDate(timeZone) > maxDate.toDate(timeZone)) {
            return { isValid: false, errorType: "maxDate" };
          }
        }

        return { isValid: true };
      },
    [min, max, timeZone],
  );

  const handleDateValidation = React.useCallback(
    (dateStr: string) => {
      const result = validateDateString(
        dateStr,
        min as CalendarDate | undefined,
        max as CalendarDate | undefined,
      );
      if (!result.isValid && result.errorType) {
        handleInvalidDate?.(result.errorType);
      }
      return result.isValid;
    },
    [validateDateString, min, max, handleInvalidDate],
  );

  // Handle date changes and update masked input
  const handleValueChange = React.useCallback(
    (details: any) => {
      onValueChange?.(details);
      if (selectionMode === "single") {
        onChange?.(new Date(details.valueAsString[0]));
        setValue(details.valueAsString[0]);
      } else {
        onChange?.(details.valueAsString?.map((date: string) => date));
      }
    },
    [onChange, onValueChange, selectionMode, setValue],
  );

  // Validate and update date on input blur
  const handleInputBlur = React.useCallback(
    (setDatePickerValue: any, focusedValue: any) => {
      if (value?.match(/\d{2}\/\d{2}\/\d{4}/)) {
        const isValid = handleDateValidation(value);
        if (isValid) {
          setDatePickerValue([focusedValue]);
        }
      }
    },
    [value, handleDateValidation],
  );

  // Add a memoized invalid state
  const isInvalid = React.useMemo(() => {
    if (!value?.match(/\d{2}\/\d{2}\/\d{4}/)) {
      return error;
    }
    return !handleDateValidation(value) || error;
  }, [value, error, handleDateValidation]);

  return (
    <Field.Root {...fieldProps} invalid={isInvalid}>
      <Field.Context>
        {({ invalid }) => {
          const effectiveError = invalid || error;
          return (
            <BaseDatePicker.Root
              locale={locale}
              min={min}
              max={max}
              timeZone={timeZone}
              {...effectiveProps}
              className={clsx("ox-reset ox-DatePickerRoot", classNames?.root)}
              selectionMode={selectionMode}
              data-selection-mode={selectionMode}
              positioning={positioning}
              onValueChange={handleValueChange}
            >
              {label ? (
                <BaseDatePicker.Label asChild>
                  <FormLabel
                    label={label}
                    size={size}
                    error={effectiveError}
                    classNames={labelClassNames}
                  />
                </BaseDatePicker.Label>
              ) : null}
              <BaseDatePicker.Control
                className={clsx(
                  "ox-reset ox-DatePickerControl",
                  classNames?.control,
                )}
              >
                <BaseDatePicker.Context>
                  {({
                    getInputProps,
                    focusedValue,
                    setValue: setDatePickerValue,
                  }) => {
                    const { onBlur, onKeyDown, type, ...inputProps } =
                      getInputProps();
                    return (
                      <>
                        {selectionMode === "single" ||
                        selectionMode === "multiple" ? (
                          <DatePickerInput
                            index={0}
                            ref={effectiveDatePickerRef}
                            {...inputProps}
                            onBlur={() =>
                              handleInputBlur(setDatePickerValue, focusedValue)
                            }
                            className={clsx(
                              "ox-reset ox-DatePickerInput",
                              classNames?.trigger,
                            )}
                            size={size}
                            error={effectiveError}
                            variant={inputVariant}
                            placeholder={placeholder}
                            rightSection={
                              <BaseDatePicker.Trigger
                                data-error={effectiveError}
                                className={clsx(
                                  "ox-reset ox-DatePickerTrigger",
                                  classNames?.trigger,
                                )}
                                data-selection-mode={selectionMode}
                                data-size={size}
                              >
                                {calendarIcon}
                              </BaseDatePicker.Trigger>
                            }
                          />
                        ) : (
                          <React.Fragment>
                            <DatePickerInput
                              index={0}
                              {...inputProps}
                              ref={effectiveDatePickerRef}
                              className={clsx(
                                "ox-reset ox-DatePickerInput",
                                classNames?.trigger,
                              )}
                              variant={inputVariant}
                              size={size}
                              error={effectiveError}
                            />

                            <DatePickerInput
                              index={1}
                              {...inputProps}
                              className={clsx(
                                "ox-reset ox-DatePickerInput",
                                classNames?.trigger,
                              )}
                              ref={effectiveDatePickerRef}
                              size={size}
                              variant={inputVariant}
                              error={effectiveError}
                            />

                            <BaseDatePicker.Trigger
                              className={clsx(
                                "ox-reset ox-FormIcon ox-DatePickerTrigger",
                                classNames?.trigger,
                              )}
                              data-selection-mode={selectionMode}
                              data-size={size}
                            >
                              {calendarIcon}
                            </BaseDatePicker.Trigger>
                          </React.Fragment>
                        )}
                      </>
                    );
                  }}
                </BaseDatePicker.Context>
              </BaseDatePicker.Control>

              <Portal>
                <BaseDatePicker.Positioner className="ox-reset ox-DatePickerPositioner">
                  <BaseDatePicker.Content
                    className={clsx(
                      "ox-reset ox-DatePickerContent",
                      classNames?.content,
                    )}
                  >
                    {/* Day view  */}
                    <BaseDatePicker.View
                      className={clsx(
                        "ox-reset ox-DatePickerContentView",
                        classNames?.view,
                      )}
                      view="day"
                    >
                      <BaseDatePicker.Context>
                        {(datePicker) => (
                          <>
                            <BaseDatePicker.ViewControl className="ox-reset ox-DatePickerViewControl">
                              <BaseDatePicker.PrevTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {leftArrow}
                                </IconButton>
                              </BaseDatePicker.PrevTrigger>
                              <BaseDatePicker.ViewTrigger className="ox-reset ox-DatePickerViewTrigger">
                                <BaseDatePicker.RangeText className="ox-reset ox-DatePickerRangeText" />
                              </BaseDatePicker.ViewTrigger>
                              <BaseDatePicker.NextTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {rightArrow}
                                </IconButton>
                              </BaseDatePicker.NextTrigger>
                            </BaseDatePicker.ViewControl>
                            <BaseDatePicker.Table className="ox-reset ox-DatePickerTable">
                              <BaseDatePicker.TableHead className="ox-reset ox-DatePickerTableHead">
                                <BaseDatePicker.TableRow className="ox-reset ox-DatePickerTableRow">
                                  {datePicker.weekDays.map((weekDay, id) => (
                                    <BaseDatePicker.TableHeader
                                      key={id}
                                      className="ox-reset ox-DatePickerTableHeader"
                                    >
                                      {weekDay.short}
                                    </BaseDatePicker.TableHeader>
                                  ))}
                                </BaseDatePicker.TableRow>
                              </BaseDatePicker.TableHead>
                              <BaseDatePicker.TableBody className="ox-reset ox-DatePickerTableBody">
                                {datePicker.weeks.map((week, id) => (
                                  <BaseDatePicker.TableRow
                                    key={id}
                                    className="ox-reset ox-DatePickerTableRow"
                                  >
                                    {week.map((day, id) => (
                                      <BaseDatePicker.TableCell
                                        key={id}
                                        value={day}
                                        className="ox-reset ox-DatePickerTableCell"
                                      >
                                        <BaseDatePicker.TableCellTrigger className="ox-reset ox-DatePickerTableCellTrigger">
                                          {day.day}
                                        </BaseDatePicker.TableCellTrigger>
                                      </BaseDatePicker.TableCell>
                                    ))}
                                  </BaseDatePicker.TableRow>
                                ))}
                              </BaseDatePicker.TableBody>
                            </BaseDatePicker.Table>
                          </>
                        )}
                      </BaseDatePicker.Context>
                    </BaseDatePicker.View>
                    {/* Month View */}
                    <BaseDatePicker.View
                      className={clsx(
                        "ox-reset ox-DatePickerContentView",
                        classNames?.view,
                      )}
                      view="month"
                    >
                      <BaseDatePicker.Context>
                        {(datePicker) => (
                          <>
                            <BaseDatePicker.ViewControl className="ox-reset ox-DatePickerViewControl">
                              <BaseDatePicker.PrevTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {leftArrow}
                                </IconButton>
                              </BaseDatePicker.PrevTrigger>
                              <BaseDatePicker.ViewTrigger className="ox-reset ox-DatePickerViewTrigger">
                                <BaseDatePicker.RangeText className="ox-reset ox-DatePickerRangeText" />
                              </BaseDatePicker.ViewTrigger>
                              <BaseDatePicker.NextTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {rightArrow}
                                </IconButton>
                              </BaseDatePicker.NextTrigger>
                            </BaseDatePicker.ViewControl>
                            <BaseDatePicker.Table className="ox-reset ox-DatePickerTable">
                              <BaseDatePicker.TableBody className="ox-reset ox-DatePickerTableBody">
                                {datePicker
                                  .getMonthsGrid({
                                    columns: 4,
                                    format: "short",
                                  })
                                  .map((months, id) => (
                                    <BaseDatePicker.TableRow
                                      key={id}
                                      className="ox-reset ox-DatePickerTableRow"
                                    >
                                      {months.map((month, id) => (
                                        <BaseDatePicker.TableCell
                                          key={id}
                                          value={month.value}
                                          className="ox-reset ox-DatePickerTableCell"
                                        >
                                          <BaseDatePicker.TableCellTrigger className="ox-reset ox-DatePickerTableCellTrigger">
                                            {month.label}
                                          </BaseDatePicker.TableCellTrigger>
                                        </BaseDatePicker.TableCell>
                                      ))}
                                    </BaseDatePicker.TableRow>
                                  ))}
                              </BaseDatePicker.TableBody>
                            </BaseDatePicker.Table>
                          </>
                        )}
                      </BaseDatePicker.Context>
                    </BaseDatePicker.View>
                    {/* Year View */}
                    <BaseDatePicker.View
                      className={clsx(
                        "ox-reset ox-DatePickerContentView",
                        classNames?.view,
                      )}
                      view="year"
                    >
                      <BaseDatePicker.Context>
                        {(datePicker) => (
                          <>
                            <BaseDatePicker.ViewControl className="ox-reset ox-DatePickerViewControl">
                              <BaseDatePicker.PrevTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {leftArrow}
                                </IconButton>
                              </BaseDatePicker.PrevTrigger>
                              <BaseDatePicker.ViewTrigger className="ox-reset ox-DatePickerViewTrigger">
                                <BaseDatePicker.RangeText className="ox-reset ox-DatePickerRangeText" />
                              </BaseDatePicker.ViewTrigger>
                              <BaseDatePicker.NextTrigger asChild>
                                <IconButton
                                  size="sm"
                                  variant="tertiary"
                                  color="gray"
                                >
                                  {rightArrow}
                                </IconButton>
                              </BaseDatePicker.NextTrigger>
                            </BaseDatePicker.ViewControl>
                            <BaseDatePicker.Table className="ox-reset ox-DatePickerTable">
                              <BaseDatePicker.TableBody className="ox-reset ox-DatePickerTableBody">
                                {datePicker
                                  .getYearsGrid({
                                    columns: 4,
                                  })
                                  .map((years, id) => (
                                    <BaseDatePicker.TableRow
                                      key={id}
                                      className="ox-reset ox-DatePickerTableRow"
                                    >
                                      {years.map((year, id) => (
                                        <BaseDatePicker.TableCell
                                          key={id}
                                          value={year.value}
                                          className="ox-reset ox-DatePickerTableCell"
                                        >
                                          <BaseDatePicker.TableCellTrigger className="ox-reset ox-DatePickerTableCellTrigger">
                                            {year.label}
                                          </BaseDatePicker.TableCellTrigger>
                                        </BaseDatePicker.TableCell>
                                      ))}
                                    </BaseDatePicker.TableRow>
                                  ))}
                              </BaseDatePicker.TableBody>
                            </BaseDatePicker.Table>
                          </>
                        )}
                      </BaseDatePicker.Context>
                    </BaseDatePicker.View>
                    {/* Footer with buttons */}
                    {withFooter ? (
                      <BaseDatePicker.Context>
                        {(context) => (
                          <div className={clsx("ox-reset ox-DatePickerFooter")}>
                            <Button
                              variant="secondary"
                              className={clsx("ox-reset ox-DatePickerButton")}
                              onClick={(e) => {
                                context.setOpen(false);
                                handleCancel(e);
                              }}
                            >
                              {labels?.cancel}
                            </Button>
                            <Button
                              className={clsx("ox-reset ox-DatePickerButton")}
                              onClick={(e) => {
                                context.setOpen(false);
                                handleConfirm(e);
                              }}
                            >
                              {labels?.confirm}
                            </Button>
                          </div>
                        )}
                      </BaseDatePicker.Context>
                    ) : null}
                  </BaseDatePicker.Content>
                </BaseDatePicker.Positioner>
              </Portal>
              {helperText?.message ? (
                <Field.HelperText
                  data-size={size}
                  data-status={helperText?.status}
                  className={clsx(
                    "ox-reset ox-FormHelperText",
                    classNames?.helperText,
                  )}
                  data-invalid={helperText?.status === "error" ? true : false}
                >
                  {getFormHelperText(helperText, size)}
                </Field.HelperText>
              ) : null}
            </BaseDatePicker.Root>
          );
        }}
      </Field.Context>
    </Field.Root>
  );
});

DatePicker.displayName = "DatePicker";
export { DatePicker, parseDate };
export type { DatePickerProps };
